import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { PATH } from 'src/features/navigation/utils/constants';
import { useListPayStationOperations } from 'src/features/payment/api/useListPayStationOperations';
import CellPayStationOperationActions from 'src/features/payment/components/cell/CellPayStationOperationActions';
import {
    payStationDeviceOptions,
    payStationStageBadgeOptions,
    payStationStatusBadgeOptions,
    payStationTypeBadgeOptions,
} from 'src/features/payment/utils/constants';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStationOperation } from 'src/features/payment/utils/types';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import CellLinkWithIcon from 'src/features/membership/components/cell/CellLinkWIthIcon';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useListPayStations } from 'src/features/payment/api/useListPayStations';
import React from 'react';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

export default function OperationsTable() {
    const { payStationId = '' } = useParams();
    const { formatMessage, formatNumber } = useIntl();

    const accessActiveStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);
    const accessPastStaysStays = useHasPermissions([PERMISSION.ACCESS_ACTIVE_STAYS]);

    const { querySortingOptions, ...sorting } = useSorting({ initialSortingState: [{ id: 'created_at', desc: true }] });
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { parkingId } = useParking();

    const { data, isLoading, isError } = useListPayStationOperations({
        parkingId,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...queryPaginationOptions,
        ...(payStationId && { pay_station: payStationId }),
    });

    const { data: payStationsData } = useListPayStations({ parkingId });

    const payStationFilterOptions = React.useMemo(
        () =>
            payStationsData?.results?.map((station) => {
                return { value: station.id, label: station?.name };
            }),
        [payStationsData],
    );

    const columnHelper = createColumnHelper<PayStationOperation>();
    const columns = [
        columnHelper.accessor('created_at', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(paymentsMessages.date),
            },
        }),
        columnHelper.accessor('ref', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(paymentsMessages.reference),
            },
        }),
        columnHelper.accessor('pay_station.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: !payStationId,
            meta: {
                title: formatMessage(paymentsMessages.payStation),
                ...(!payStationId && { options: payStationFilterOptions }),
            },
            id: 'pay_station',
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.type),
                badgeOptions: payStationTypeBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('stage', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.stage),
                badgeOptions: payStationStageBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('status', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(paymentsMessages.status),
                badgeOptions: payStationStatusBadgeOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.display({
            id: 'stay',
            header: DataTableColumnHeader,
            cell: ({ row: { original } }) => {
                const activeStayId = original.active_stay?.id;
                const pastStayId = original.past_stay?.id;

                if (!activeStayId && !pastStayId) return '-';

                const urlPath = activeStayId
                    ? `/${PATH.VISITORS}/active-stays/dashboard/${activeStayId}`
                    : `/${PATH.VISITORS}/past-stays/dashboard/${pastStayId}`;

                const hasPermission = activeStayId ? accessActiveStays : accessPastStaysStays;

                return <CellLinkWithIcon toPath={urlPath} linkTitle={original.ref} hasPermissions={hasPermission} />;
            },
            meta: {
                title: formatMessage(paymentsMessages.stay),
            },
        }),
        columnHelper.accessor('requested_amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({
                row: {
                    original: { requested_amount, currency },
                },
            }) => {
                if (!requested_amount) return '-';
                return formatNumber(requested_amount / 100, {
                    style: 'currency',
                    currency,
                });
            },
            meta: {
                title: formatMessage(paymentsMessages.requestedAmount),
            },
        }),
        columnHelper.accessor('device_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            cell: (info) => {
                const value = info.getValue();
                if (typeof value !== 'number') return '-';
                return formatMessage(payStationDeviceOptions[value]);
            },
            meta: {
                title: formatMessage(paymentsMessages.deviceType),
                options: Object.entries(payStationDeviceOptions).map(([key, value]) => {
                    return { value: key, label: formatMessage(value) };
                }),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellPayStationOperationActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<PayStationOperation, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={data}
            sorting={sorting}
            pagination={pagination}
            search={search}
            filters={filters}
            enableSearchParams={false}
            enableLocalFilters={false}
            semantics={{
                title: formatMessage(paymentsMessages.operations),
                entryName: formatMessage(paymentsMessages.operation).toLowerCase(),
                entryNames: formatMessage(paymentsMessages.operations).toLowerCase(),
            }}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
            }}
        />
    );
}
